import React from "react";
import ConfirmationModal from "../ConfirmationModal.jsx";
import ValidateSheetButton from "./ValidateSheetButton.jsx";
import { SECONDARY } from "./Fields.jsx";
import { preloadKey, runPreloadBatch } from "../../utils/consolidationPreloadBatch.js";
import { eveningGroups } from "../../utils/consolidationPreloadEvening.js";
import useConsolidationCapture from "../../hooks/useConsolidationCapture.js";
import useConsolidationDrafts from "../../hooks/useConsolidationDrafts.jsx";
import { startPreloadBatchCapture } from "../../utils/consolidationCaptures.js";

export const PreloadCoordination = React.createContext(null);

// Les soirées des employés (PreloadEvening, kind « service ») suivent les mêmes actions communes que leurs préchargements.
export default function PreloadCoordinator({ preloads, defaultEmployeeId = null, rights, busy, run, onChanged, children }) {
  const store = useConsolidationDrafts();
  const registry = React.useRef(new Map());
  const running = React.useRef(false);
  const [, redraw] = React.useReducer(value => value + 1, 0);
  const coordination = React.useMemo(() => ({
    register(key, handle) { registry.current.set(key, handle); redraw(); return () => { registry.current.delete(key); redraw(); }; },
    changed: redraw,
    // Valeurs en cours d'une ligne, lues au moment d'un geste (recopie Geotab).
    get: key => registry.current.get(key)?.current,
  }), []);
  const entries = preloads.map(row => registry.current.get(preloadKey(row))?.current).filter(Boolean);
  const services = [...registry.current.values()].map(handle => handle.current).filter(handle => handle?.kind === "service");
  const drafts = [...entries, ...services].filter(entry => entry.row.status === "draft");
  const modified = drafts.filter(entry => entry.dirty);
  const [confirming, setConfirming] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const disabled = busy || entries.length !== preloads.length;
  const editable = drafts.filter(entry => entry.baseline?.day_editable !== false);
  const savingError = modified.find(entry => entry.saveError)?.saveError;
  const validationReasons = [...new Set([
    ...(disabled ? ["Une opération est en cours."] : []),
    ...drafts.map(entry => entry.dayError).filter(Boolean),
    ...drafts.map(entry => entry.validationError).filter(Boolean),
  ])];
  const capture = useConsolidationCapture();
  // Annuler reprend aussi la soirée qu'un changement d'employé a masquée : elle revient avec ses préchargements enregistrés,
  // sans les heures saisies avant (chaque soirée affichée efface la sienne).
  const cancel = () => {
    drafts.forEach(entry => entry.reset());
    eveningGroups(preloads, defaultEmployeeId).forEach(group => { if (store?.readDraft(group.key)) store.put(group.key, null); });
    setResult(null);
  };
  const execute = (validate) => {
    if (running.current) return;
    running.current = true;
    const tracked = startPreloadBatchCapture(capture, entries, validate);
    const operation = { saved: [], validated: [], error: "", pending: true };
    setResult({ ...operation });
    return run(async () => {
      try {
        await runPreloadBatch(tracked?.watch(entries) || entries, { validate, services, refresh: () => onChanged({ propagateError: true }),
          report: (entry, kind) => { tracked?.report(entry, kind); operation[kind].push(entry.label); setResult({ ...operation }); } });
        // « Sauvegarder » réussi : toute la journée sort de la saisie, seul « Modifier » reste en bas.
        if (!validate) [...entries, ...services].forEach(entry => entry.view?.());
        tracked?.finish();
      } catch (error) {
        tracked?.finish(error);
        // Déjà dit sous les boutons : pas de cadre rouge en haut de la page.
        if (error && typeof error === "object") error.shown = true;
        operation.error = `${error.saveUnconfirmed ? (error.unconfirmedAction === "validate" ? "Validation non confirmée. " : "Sauvegarde non confirmée. ") : ""}${error.message}`;
        throw error;
      } finally {
        running.current = false;
        setResult({ ...operation, pending: false });
      }
    });
  };
  return <PreloadCoordination.Provider value={coordination}>
    {children}
    {preloads.length ? <footer aria-label="Actions communes des préchargements" title="Tous les préchargements de la journée." className="mt-2 flex flex-wrap items-center gap-2">
      {rights.write && drafts.length ? <>
        {drafts.some(entry => !entry.editing) ? <button type="button" className={SECONDARY} disabled={disabled || !editable.length} onClick={() => editable.forEach(entry => entry.edit())}>Modifier</button> : null}
        {drafts.some(entry => entry.editing) ? <button type="button" className={SECONDARY} disabled={disabled || !modified.length || Boolean(savingError)} title={savingError || undefined} onClick={() => execute(false)}>Sauvegarder</button> : null}
        <button type="button" className={SECONDARY} disabled={disabled || !editable.length || drafts.some(entry => entry.concurrentChange)} onClick={() => setConfirming(true)}>Vider les saisies</button>
        {drafts.some(entry => entry.editing && (entry.dirty || entry.row.id)) ? <button type="button" className={SECONDARY} disabled={disabled} onClick={cancel}>Annuler les modifications</button> : null}
      </> : null}
      {rights.validate && drafts.some(entry => entry.kind !== "service") ? <ValidateSheetButton reasons={validationReasons} subject="les préchargements" buttonLabel="Valider les préchargements" onValidate={() => execute(true)} /> : null}
      {result ? <div role={result.error ? "alert" : "status"} className={`w-full text-xs ${result.error ? "text-red-700" : "text-slate-600"}`}>
        {result.pending ? <p>Traitement en cours…</p> : null}
        {result.saved.length ? <p>Enregistrés : {result.saved.join(" ; ")}.</p> : null}
        {result.validated.length ? <p>Validés : {result.validated.join(" ; ")}.</p> : null}
        {result.error ? <p>{result.error} La suite a été arrêtée ; les saisies non traitées sont conservées.</p> : null}
      </div> : null}
    </footer> : null}
    {confirming ? <ConfirmationModal title="Vider les saisies ?" eyebrow="Consolidation" tone="danger" cornerRadius="grain" panelSize="narrow" contentDensity="compact"
      description="Vider les heures et les tonnages de tous les préchargements modifiables de cette journée. Les préchargements validés sont conservés."
      warningMessage="Les changements restent locaux jusqu’à la sauvegarde. Les confirmations de chargement sont conservées."
      cancelLabel="Conserver les valeurs" confirmLabel="Vider les saisies" onCancel={() => setConfirming(false)} onClose={() => setConfirming(false)}
      onConfirm={() => { if (!disabled) editable.forEach(entry => entry.clear()); setConfirming(false); setResult(null); }} /> : null}
  </PreloadCoordination.Provider>;
}
